import navPersonal from '../../../../assets/brand/edit-hub/nav-personal.svg';
import navSchool from '../../../../assets/brand/edit-hub/nav-school.svg';
import navTeams from './icons/nav-teams.svg';
import navBilling from './icons/nav-billing.svg';
import { STATUS_META, planState } from './subscriptionStatus';
import { displayTeamName, stripSeasonSuffix } from '../../../../utils/teamNames';

// Coach "Profile & verification" hub (Figma "Coach Edit Profile", Hub model
// v2): the verification card and the section list on the left, the open
// section on the right. Pure helpers, unit-tested in coachProfileModel.test.js.

export const SECTIONS = [
  { id: 'account', group: 'Account', label: 'Account information', icon: navPersonal },
  { id: 'team', group: 'Team', label: 'Team settings', icon: navSchool },
  { id: 'additional', group: 'Team', label: 'Additional teams', icon: navTeams },
  { id: 'subscription', group: 'Settings', label: 'Subscription', icon: navBilling },
];
export const GROUPS = ['Account', 'Team', 'Settings'];

export const sectionById = (id) => SECTIONS.find((s) => s.id === id) || SECTIONS[0];
export const nextSection = (id) => SECTIONS[SECTIONS.findIndex((s) => s.id === id) + 1] || null;

export const joinParts = (...parts) => parts.filter(Boolean).join(' · ');

// The coach's team by the one display rule (utils/teamNames, D15): its
// displayName, else its name with the age group its league key names,
// never a season CACHE added.
export const teamDisplayOf = (team) => {
  const name = String(team?.name || '').trim();
  if (!team || !(name || team.displayName)) return name;
  return String(displayTeamName({ ...team, teamName: name, name }) || name).trim();
};

// Team settings, in the Figma order; rows without a value are left out.
export const teamSettingsRows = (team) => {
  if (!team) return [];
  return [
    { label: 'Sport', value: joinParts(team.sport, team.gender) },
    { label: 'Tier', value: team.tier },
    { label: 'Region', value: team.region },
    { label: 'Governing body / League', value: team.body },
    { label: 'Sub-division', value: team.subDivision },
    { label: 'Class', value: team.classDivision },
    { label: 'Conference', value: team.conference },
    // A stored label keeps no season CACHE once added (" · 2026").
    { label: 'League', value: stripSeasonSuffix(team.leagueLabel || '') },
    { label: 'Current team', value: teamDisplayOf(team), requested: !!team.requested },
    // A long id: on phones it sits under its label and breaks anywhere. One
    // label for every source: coaches never see the data provider's name.
    { label: 'Team ID', value: team.teamId, stack: true },
  ].filter((row) => row.value);
};

export const teamMetaLine = (team) => joinParts(stripSeasonSuffix(team.leagueLabel || ''), team.tier, team.subDivision);

export const NAME_MAX = 60;

// Same rules as PATCH coach/profile/.
export const validateAccount = ({ firstName, lastName }) => {
  const problems = {};
  [['firstName', firstName, 'Enter your first name.'], ['lastName', lastName, 'Enter your last name.']].forEach(([key, value, missing]) => {
    const text = (value || '').trim();
    if (!text) problems[key] = missing;
    else if (text.length > NAME_MAX) problems[key] = `Keep names under ${NAME_MAX} characters.`;
  });
  return problems;
};

export const MAX_SHARE = 20;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Addresses typed into "Send your code by email": split on commas,
// semicolons or spaces, lower-cased, duplicates dropped.
export const parseEmails = (text) => {
  const valid = [];
  const invalid = [];
  const seen = new Set();
  String(text || '').split(/[\s,;]+/).forEach((part) => {
    const value = part.trim().toLowerCase();
    if (!value || seen.has(value)) return;
    seen.add(value);
    (EMAIL_RE.test(value) ? valid : invalid).push(value);
  });
  return { valid, invalid };
};

// Why the typed addresses cannot be sent yet, or ''.
export const shareProblem = ({ valid, invalid }) => {
  if (invalid.length) return `Check these addresses: ${invalid.join(', ')}`;
  if (!valid.length) return "Enter your athletes' email addresses.";
  if (valid.length > MAX_SHARE) return `Send to ${MAX_SHARE} addresses or fewer at a time.`;
  return '';
};

// Stripe sends unix seconds; stored dates come as ISO strings without a
// zone, which are UTC.
export const toDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value * 1000);
  const text = String(value);
  const date = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(text) ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

// Status, Plan and (during a trial) Trial ends, from GET /entitlement/.
export const subscriptionRows = (entitlement = {}, now = new Date()) => {
  const { status, hasPlan, statusMeta } = planState(entitlement);
  const inTrial = status === 'trial' || status === 'trialing';
  const trialEnd = inTrial ? toDate(entitlement.trialEnd || (status === 'trialing' ? entitlement.nextPaymentDate : null)) : null;
  const trialOver = !!trialEnd && trialEnd < now;
  const waiverUntil = toDate(entitlement.promoUntil);
  const waived = entitlement.promoWaiver === true && (!waiverUntil || waiverUntil > now);

  let statusLabel = statusMeta?.label || 'Free';
  if (waived) statusLabel = 'Fee waived';
  else if (trialOver) statusLabel = 'Trial ended';
  // A new account's own trial ('trial'), before any Stripe subscription.
  else if (status === 'trial') statusLabel = STATUS_META.trialing.label;

  const rows = [
    { label: 'Status', value: statusLabel },
    { label: 'Plan', value: hasPlan ? entitlement.displayName || 'Paid plan' : 'No active plan' },
  ];
  if (inTrial && trialEnd && !trialOver && !waived) rows.push({ label: 'Trial ends', value: formatDate(trialEnd) });
  return rows;
};

// The plan rows the profile shows, from the entitlement store: the live plan
// once it has loaded, the account's own record (`subscription`) when it could
// not load, and null while it loads.
export const planRows = (entitlement, subscription, now = new Date()) => {
  if (entitlement?.entitlement) return subscriptionRows(entitlement, now);
  if (entitlement?.error) return subscriptionRows({ subscriptionStatus: subscription?.status, trialEnd: subscription?.trialEnd }, now);
  return null;
};

export const NO_TEAM = 'No team selected yet.';
export const NO_ADDITIONAL_TEAMS = 'No additional teams assigned yet.';
export const PLAN_LOADING = 'Loading…';

const fullName = (account = {}) => [account.firstName, account.lastName].map((part) => (part || '').trim()).filter(Boolean).join(' ');

// The support line under each row of the phone hub (Figma 00 / 00a, D-c6),
// built from what the hub already loaded: the saved name, the team and its
// sub-division, the additional teams (one by name, more as a count, plus a
// league-admin grant), and the plan's status and name. `entitlement` is the
// entitlement store and `subscription` the account's own record, read as the
// Subscription section reads them.
export const sectionSummary = (id, profile = {}, entitlement = null, { subscription, now } = {}) => {
  if (id === 'account') return fullName(profile.account) || profile.account?.email || '';
  if (id === 'team') {
    const rows = teamSettingsRows(profile.team);
    if (!rows.length) return NO_TEAM;
    const { team } = profile;
    return joinParts(teamDisplayOf(team), team.subDivision) || teamMetaLine(team) || rows[0].value;
  }
  if (id === 'additional') {
    const teams = profile.additionalTeams || [];
    const { leagueAdmin } = profile;
    if (!teams.length && !leagueAdmin) return NO_ADDITIONAL_TEAMS;
    let teamPart = null;
    if (teams.length === 1) teamPart = teamDisplayOf(teams[0]) || '1 team';
    else if (teams.length > 1) teamPart = `${teams.length} teams`;
    return joinParts(leagueAdmin && joinParts(leagueAdmin.label, 'League admin'), teamPart);
  }
  if (id === 'subscription') {
    const rows = planRows(entitlement, subscription, now);
    return rows ? joinParts(...rows.slice(0, 2).map((row) => row.value)) : PLAN_LOADING;
  }
  return '';
};
